import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, LayersControl, Polygon, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';

// Fix default icon assets (bundled via CDN)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const { BaseLayer } = LayersControl;

/**
 * DrawControl — adds a leaflet-draw polygon control and reports polygon
 * lat/lon rings up through `onPolygon`.
 */
const DrawControl = ({ onPolygon, defaultRing }) => {
  const map = useMap();
  const drawnRef = useRef(null);

  useEffect(() => {
    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    drawnRef.current = drawn;

    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: '#facc15', weight: 3, fillOpacity: 0.15 },
        },
        rectangle: { shapeOptions: { color: '#facc15' } },
        polyline: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: { featureGroup: drawn, remove: true },
    });
    map.addControl(drawControl);

    const setFromLayer = (layer) => {
      const latlngs = layer.getLatLngs()[0];
      const ring = latlngs.map((ll) => [ll.lng, ll.lat]);
      onPolygon(ring);
    };

    map.on(L.Draw.Event.CREATED, (e) => {
      drawn.clearLayers();
      drawn.addLayer(e.layer);
      setFromLayer(e.layer);
    });
    map.on(L.Draw.Event.EDITED, (e) => {
      e.layers.eachLayer(setFromLayer);
    });
    map.on(L.Draw.Event.DELETED, () => onPolygon(null));

    // Preload default polygon if any
    if (defaultRing && defaultRing.length >= 3) {
      const latlngs = defaultRing.map(([lng, lat]) => [lat, lng]);
      const poly = L.polygon(latlngs, { color: '#facc15', weight: 3, fillOpacity: 0.15 });
      drawn.addLayer(poly);
      map.fitBounds(poly.getBounds(), { padding: [20, 20] });
    }

    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawn);
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

/**
 * RecenterOnCenter — animates the map to a new center prop (used when user
 * types coordinates manually).
 */
const RecenterOnCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) map.setView(center, Math.max(map.getZoom(), 15));
  }, [center, map]);
  return null;
};

const FarmMap = ({ center, onPolygon, defaultRing, height = 380 }) => (
  <div data-testid="farm-map" className="rounded-xl overflow-hidden border border-icrisat-green/30 shadow-sm">
    <MapContainer
      center={center || [17.9784, 79.5941]}
      zoom={15}
      style={{ height, width: '100%' }}
      scrollWheelZoom={true}
    >
      <LayersControl position="topleft">
        <BaseLayer checked name="Google Satellite">
          {/* Google satellite hybrid tiles (roads + labels overlay). */}
          <TileLayer
            url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            attribution='Imagery © Google · Map data © Google, ICRISAT'
            maxZoom={20}
          />
        </BaseLayer>
        <BaseLayer name="Google Satellite (no labels)">
          <TileLayer
            url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
            attribution='Imagery © Google'
            maxZoom={20}
          />
        </BaseLayer>
        <BaseLayer name="Esri World Imagery">
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles © Esri"
            maxZoom={19}
          />
        </BaseLayer>
        <BaseLayer name="OpenStreetMap">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM contributors" />
        </BaseLayer>
      </LayersControl>

      {center && <Marker position={center} />}
      <DrawControl onPolygon={onPolygon} defaultRing={defaultRing} />
      <RecenterOnCenter center={center} />
    </MapContainer>
  </div>
);

export default FarmMap;
